import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import { number, string } from "prop-types";

import { STATUSES } from "../../constants";
import { dispatchListingProgressChanged } from "../../events/listingProgressChanged";
import { respondToListingSaved } from "../../events/listingSaved";
import { respondToTourRatingAdded } from "../../events/tourRatingAdded";
import ListingProgressMenu from "../ListingProgressMenu";

export const UPDATE_LISTING_JOURNEY = gql`
  mutation update_listing_journey($input: UpdateListingJourneyInput!) {
    update_listing_journey(input: $input) {
      listing_journey {
        id
        status
      }
    }
  }
`;

export const CREATE_LISTING_JOURNEY = gql`
  mutation create_listing_journey($input: CreateListingJourneyInput!) {
    create_listing_journey(input: $input) {
      listing_journey {
        id
        status
      }
    }
  }
`;

export const STOP_LISTING_JOURNEY = gql`
  mutation stop_listing_journey($listing_journey_id: ID!) {
    stop_listing_journey(listing_journey_id: $listing_journey_id)
  }
`;

const pushChangeProgressToDataLayer = (
  listingJourneyId,
  oldStatus,
  newStatus
) => {
  window.dataLayer.push({
    event: "custom_event",
    event_category: "my_activity",
    event_action: "change_progress",
    event_label: `id=${listingJourneyId}|status_from=${oldStatus}|status_to=${newStatus}`
  });
};

const ListingProgressMenuContainer = props => {
  const { listingId, listingType } = props;
  const [status, updateStatus] = useState(props.status);
  const [listingJourneyId, updateListingJourneyId] = useState(
    props.listingJourneyId
  );
  respondToTourRatingAdded(
    { listingId, listingType },
    () => {
      const allowedStatuses = [STATUSES.saved.value, STATUSES.messaged.value];
      if (allowedStatuses.includes(status)) {
        updateStatus(STATUSES.toured.value);
      }
    },
    [status]
  );

  respondToListingSaved(
    { listingId, listingType },
    e => {
      updateStatus(e.detail.status);
      updateListingJourneyId(e.detail.id);
    },
    [status]
  );

  const [updateListingJourney] = useMutation(UPDATE_LISTING_JOURNEY, {
    onCompleted: ({ update_listing_journey: { listing_journey: result } }) => {
      updateStatus(result.status);
    }
  });

  const handleClick = () => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "custom_event",
        event_category: "my_activity",
        event_action: "click_progress",
        event_label: `id=${listingJourneyId}|status_from=${status}`
      });
    }
  };

  const handleChange = event => {
    const { value: newStatus } = event.target;

    if (window.dataLayer) {
      pushChangeProgressToDataLayer(listingJourneyId, status, newStatus);
    }

    dispatchListingProgressChanged({
      listingId,
      listingType,
      status: newStatus
    });

    updateListingJourney({
      variables: {
        input: {
          listing_journey_id: listingJourneyId,
          status: newStatus
        }
      }
    });
  };

  if (!listingJourneyId) return null;

  return (
    <ListingProgressMenu
      listingId={listingId}
      onChange={handleChange}
      onClick={handleClick}
      status={status}
    />
  );
};

ListingProgressMenuContainer.defaultProps = {
  listingJourneyId: null,
  status: null
};

ListingProgressMenuContainer.propTypes = {
  listingId: number.isRequired,
  listingType: string.isRequired,
  listingJourneyId: number,
  status: string
};

export default ListingProgressMenuContainer;
